import {
  AuthFetch,
  staticError,
  useAuthFetch,
  useGuardedEffect,
  useHasRole,
} from "@p0security/frontend-core";
import { ErrorDisplay, Logo } from "@p0security/frontend-core/react";
import * as firestoreProvider from "@p0security/frontend-core/react";
import { JiraIntegration, JiraProject } from "@p0security/integrations-core";
import { JiraIconUrl } from "@p0security/shared/integrations/registry";
import {
  Button,
  Flex,
  Select,
  Spacing,
  Spin,
  Typography,
} from "@p0security/ui";
import { Tenant } from "components/Login";
import { jwtDecode } from "jwt-decode";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { IntegrationCard } from "../IntegrationCard";
import { OwnerAlert } from "../OwnerAlert";

const { Link } = Typography;

// See usage restrictions at https://www.atlassian.com/legal/trademark#partners-and-vendors

export const JiraIcon = <Logo logo={JiraIconUrl} title="Jira" size={14} />;

const JiraProjectSelector: React.FC<{
  config: JiraIntegration;
  authFetch: AuthFetch;
  isFetching: boolean;
}> = ({ authFetch, config, isFetching }) => {
  const [projects, setProjects] = useState<JiraProject[]>();
  const canRefresh = useHasRole("owner");

  useGuardedEffect(
    (cancellation) => async () => {
      if (!canRefresh) return;
      const response = await authFetch("integrations/jira/projects", {
        method: "GET",
      });
      if (!response) return;
      const { projects } = await response.json();
      cancellation.guard(setProjects)(projects);
    },
    [authFetch, canRefresh],
    staticError
  );

  const options = useMemo(
    () => projects?.map((p) => ({ value: p.id, label: p.name })),
    [projects]
  );

  const onSelect = useCallback(
    async (id: string) => {
      const project = projects?.find((p) => p.id === id);
      if (!project) return;
      await authFetch("integrations/jira/config/project", {
        method: "PUT",
        json: project,
      });
    },
    [authFetch, projects]
  );

  return projects && !isFetching ? (
    !projects.length ? (
      <>Please create a project in your Jira instance to continue</>
    ) : (
      <>
        <div>Select the project where P0 will create issues:</div>
        <div>
          <Select
            disabled={!canRefresh}
            onChange={onSelect}
            options={options}
            placeholder="Select a project"
            /* eslint-disable-line react/forbid-component-props */ style={{
              minWidth: "20em",
            }}
            value={config.project?.id}
          />
        </div>
      </>
    )
  ) : (
    <Spin />
  );
};

export const Jira: React.FC<object> = () => {
  const tenantId = useContext(Tenant);
  const integrationData = firestoreProvider.useFirestoreDoc<JiraIntegration>(
    `o/${tenantId}/integrations/jira`,
    { live: true }
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<any>();
  const authFetch = useAuthFetch(setError, setIsFetching);
  const canInstall = useHasRole("owner");

  const jiraUrl = searchParams.get("jiraUrl");

  // Note that a phisher could presumably spoof a jwt here that wasn't signed by
  // Atlassian. However, we also validate the jwt against the Jira
  // installation's private key, so this would just lead to a backend API error.
  const jwt = searchParams.get("jwt");
  const decoded = useMemo(() => {
    if (!jwt) return undefined;
    return jwtDecode(jwt);
  }, [jwt]);

  // If we've successfully installed, remove the input query params from Jira,
  // these are no longer needed
  useEffect(() => {
    if (integrationData.doc && (jwt || jiraUrl)) {
      setSearchParams(new URLSearchParams());
    }
  }, [integrationData.doc, jiraUrl, jwt, setSearchParams]);

  const installJira = useCallback(async () => {
    await authFetch(`integrations/jira/install`, {
      method: "POST",
      json: { jwt, jiraUrl },
    });
  }, [authFetch, jiraUrl, jwt]);

  const removeIntegration = useCallback(async () => {
    const response = await authFetch("integrations/jira/config", {
      method: "DELETE",
    });
    if (!response) return;
  }, [authFetch]);

  return (
    <IntegrationCard
      integration="jira"
      logo={JiraIconUrl}
      onRemove={removeIntegration}
    >
      {error && <ErrorDisplay title="Error in Jira update" error={error} />}
      {integrationData.doc ? (
        <Flex gap="0.7em" vertical>
          <div>Installed on {integrationData.doc.data.jira.displayUrl}</div>
          <JiraProjectSelector
            config={integrationData.doc.data}
            authFetch={authFetch}
            isFetching={isFetching}
          />
        </Flex>
      ) : decoded && jiraUrl ? (
        <Flex gap="0.7em" vertical>
          <div>
            Connecting P0 to{" "}
            <Link href={jiraUrl} target="_blank" rel="noopener">
              {jiraUrl}
            </Link>
          </div>
          {canInstall ? (
            <div>
              <Button type="primary" onClick={installJira} loading={isFetching}>
                Finish installation
              </Button>
            </div>
          ) : (
            <OwnerAlert />
          )}
        </Flex>
      ) : (
        <Flex gap="0.7em" vertical>
          <Typography.Paragraph>
            Connect Jira to create and track P0 findings as issues, allowing
            findings to be assigned and managed within your Jira workflows.
          </Typography.Paragraph>
          <Typography.Text strong>Prerequisites:</Typography.Text>
          <ul className="preamble-list">
            <li>Jira admin access</li>
            <li>A Jira project where P0 will create issues</li>
          </ul>
          <Spacing asChild mt={16}>
            <Typography.Paragraph>
              To get started, install P0 from the{" "}
              <Link
                href="https://marketplace.atlassian.com/apps/1234348/p0-security?hosting=cloud&tab=installation"
                target="_blank"
                rel="noopener"
              >
                Atlassian Marketplace
              </Link>
              .
            </Typography.Paragraph>
          </Spacing>
        </Flex>
      )}
    </IntegrationCard>
  );
};
